<template>
    <b-container>
        <b-modal
            :visible="visible" 
            scrollable 
            size="xl" 
            centered
            body-class="d-flex justify-content-center p-4"
            header-class="p-3 w-100 d-flex justify-content-end"
            no-close-on-backdrop
            no-close-on-esc>
            <template #modal-header="{ close }">
                <h5 class="font-weight-semibold">{{ $t('ClientValidationType') }}</h5>
                <b-col v-if="selectedOption" class="w-100 d-flex justify-content-start align-items-center">
                    <b-icon-arrow-left @click="selectedOption = null" role="button" font-scale="1" v-b-tooltip.hover :title="$t('Back')"/> 
                    <h6 class="m-0 ml-3">{{ $t('SigningUpCertificate') }}</h6>
                </b-col>
                <b-col class="text-right">
                    <b-icon-x-lg @click="visible = false" role="button" font-scale="1" v-b-tooltip.hover :title="$t('Close')" />
                </b-col>
            </template>
            <b-row class="w-100 d-flex justify-content-center">
                <b-col v-for="option in filteredOptions" :key="option.id" cols="12" md="6" lg="4" class="mb-3">
                    <div 
                        @click="selectOption(option)"
                        @mouseover="hoveredOption = option.id"
                        @mouseleave="hoveredOption = null"
                        role="button"
                        class="option-card border rounded overflow-hidden d-flex flex-column h-100"
                        :class="{
                            'shadow-sm': hoveredOption === option.id,
                            [`border-${Variant}`]: hoveredOption === option.id
                        }">
                        <div class="video-wrapper bg-light w-100 d-flex justify-content-center align-items-center" style="height: 180px; background-color: #F8F9FA !important;">
                            <b-img
                                :src="hoveredOption === option.id ? option.icon.hover : option.icon.normal"
                                :alt="option.title"
                                class="img-fluid"
                                style="width: 80px; height: 80px; transition: transform 0.2s ease-in-out;" />
                        </div>
                        <div class="text-zone bg-white p-3 d-flex flex-column flex-grow-1 text-left">
                            <h6 :class="['text-dark', 'font-weight-bold', 'm-0', { [`text-${Variant}`]: hoveredOption === option.id }]">{{ option.title }}</h6>
                            <small class="text-muted mt-1" style="line-height: 1.2;">{{ option.description }}</small>
                        </div>
                    </div>
                </b-col>
            </b-row>
            <template #modal-footer>
                <div class="w-100 d-flex justify-content-end">
                    <b-button 
                        :variant="'outline-' + Variant" 
                        class="mr-2"
                        pill
                        @click="visible = false">
                        {{$t('Close')}}
                    </b-button>
                </div>
            </template>
        </b-modal>
        <component
            v-if="selectedOption"
            @cancel="OnCancel()"
            @validation="OnValidation()"
            :is="selectedOption.component" 
            :Certificate="Certificate"
            :Strategy="selectedOption.strategy"/>
    </b-container>
</template>

<script lang="ts">

/**
* Composant Vue js
*/
import { Component, InjectReactive, Prop, PropSync, Vue } from 'vue-property-decorator';

/**
* Classe mandat
*/
import { SMACertificate } from '@/types/Administration/SMA/SMACertificate';

/**
* Composants des options
*/
import SigningUpCertificateCustomer from './SignupOptions/SigningUpCertificateCustomer.vue';
import SigningUpCertificateSupervisor from './SignupOptions/SigningUpCertificateSupervisor.vue';
import SendCertificate from './SignupOptions/SendCertificate.vue';
import ImportSignedCertificate from './SignupOptions/ImportSignedCertificate.vue';

/**
* Traduction
*/
import locale from '../locales/SMA.json';

@Component({
    components:{
        SigningUpCertificateCustomer,
        SigningUpCertificateSupervisor,
        SendCertificate,
        ImportSignedCertificate
    },
    i18n : { messages: locale }
})
/**
* Selecteur d'option de signature du mandat
*/
export default class SignupCertificateOptions extends Vue {

    /**
     * Mandat
     */
    @Prop() 
    private Certificate!: SMACertificate;

    /**
     * Signataire
     */
    @Prop() 
    private Who!: "Customer" | "Supervisor";

    /**
     * Variant de couleur à appliquer sur le composant 
     */
    @InjectReactive()
    public Variant!: string;

    /**
     * Indique si la modal est visible
     */
    @PropSync('Visible', { type: Boolean })
    private visible!: boolean;

    /**
     * Option sélectionnée
     */
    private selectedOption: {
        id: string;
        icon: { normal: string; hover: string };
        tooltip: string;
        component: string;
        modalSize: string;
        strategy: string;
    } = null;

    private hoveredOption: string | null = null;

    // Ajout de "title" et "description" pour correspondre au design visuel
    private get allOptions() {
    return [
        { 
            id: '1',
            target: "Customer",
            icon: { 
                normal: require('@/assets/images/envelope-normal.svg'), 
                hover: require('@/assets/images/envelope-hover.svg')
            },
            tooltip: this.$t('SendingTheCertificate').toString(), 
            title: 'Envoi par mail',
            description: 'Envoyer un lien par mail au client concerné',
            component: 'SendCertificate',
            modalSize: 'md',
            strategy: null
        },
        { 
            id: '2',
            target: "Customer",
            icon: { 
                normal: require('@/assets/images/import-normal.svg'), 
                hover: require('@/assets/images/import-hover.svg')
            },
            tooltip: this.$t('CertificateImportation').toString(),  
            title: 'Import du mandat',
            description: 'Importer un mandat depuis votre ordinateur',
            component: 'ImportSignedCertificate',
            modalSize: 'md',
            strategy: 'ImportSignature'
        },
        { 
            id: '3',
            target: "Customer",
            icon: { 
                normal: require('@/assets/images/signature-normal.svg'), 
                hover: require('@/assets/images/signature-hover.svg')
            },
            tooltip: this.$t('CustomerSignature').toString(), 
            title: 'Signature directe',
            description: 'Signer le mandat depuis votre ordinateur',
            component: 'SigningUpCertificateCustomer',
            modalSize: 'xl',
            strategy: 'DefaultSignature'
        },
        { 
            id: '4', 
            target: "Supervisor",
            icon: { 
                normal: require('@/assets/images/signature-normal.svg'), 
                hover: require('@/assets/images/signature-hover.svg')
            },
            tooltip: this.$t('SupervisorSignature').toString(), 
            title: 'Signature superviseur',
            description: 'Faire signer le mandat par le superviseur',
            component: 'SigningUpCertificateSupervisor',
            modalSize: 'xl',
            strategy: 'SupervisorSignature'
        },
    ];
}

    /**
     * Lors de l'annulation de la signature
     */
    private OnCancel(): void {
        this.selectedOption = null;
    }

    /**
     * Lors de la validation de la signature
     */
    private OnValidation(): void {
        this.selectedOption = null;
        this.visible = false;
    }

    /**
     * Attribut l'option sélectionnée
     * @param option Option sélectionné
     */
    private selectOption(option: { id: string; icon: { normal: string; hover: string }; tooltip: string; component: string, modalSize: string, strategy: string }): void {
        this.selectedOption = option;
    }

    /**
     * Filtre les options suivant le signataire (Client ou Superviseur)
     */
    private get filteredOptions() {
        const allowedIds = this.Who === "Customer" ? ["1", "2", "3"] : ["4"];
        // Permet de conserver l'ordre défini dans allowedIds
        return allowedIds.map(id => this.allOptions.find(opt => opt.id === id)).filter(Boolean);
    }
}
</script>